export type AnalysisLesson = {
  title: string;
  /** Slug of an existing, published article. */
  slug: string;
};

export type AnalysisCategory = {
  id: string;
  no: string;
  title: string;
  intro: string;
  lessons: AnalysisLesson[];
  /** Grid width on the 12-column desktop editorial grid. */
  colSpan: 4 | 5 | 7 | 8;
  tone: 'primary' | 'accent' | 'secondary';
};

export const analysisHero = {
  eyebrow: 'ศูนย์การเรียนรู้การวิเคราะห์ทางเทคนิค',
  title: 'เรียนรู้การวิเคราะห์ Forex สำหรับมือใหม่',
  description:
    'ทำความเข้าใจการวิเคราะห์กราฟ Forex ตั้งแต่แท่งเทียน แนวโน้มราคา แนวรับแนวต้าน ไปจนถึงอินดิเคเตอร์และรูปแบบกราฟ พร้อมเรียนรู้วิธีนำเครื่องมือเหล่านี้มาใช้ร่วมกันอย่างเป็นระบบ',
  primaryCta: 'เริ่มเรียนรู้การวิเคราะห์กราฟ',
  secondaryCta: 'ดูบทความทั้งหมด',
  highlights: [
    { value: '4', label: 'ขั้นตอนการเรียนรู้' },
    { value: '6', label: 'หมวดบทความ' },
    { value: '30', label: 'หัวข้อในคลัง' },
  ],
  chart: {
    symbol: 'EUR/USD',
    timeframe: 'H1',
    caption: 'ภาพกราฟตัวอย่างเพื่อการเรียนรู้ ไม่ใช่ราคาแบบเรียลไทม์หรือสัญญาณซื้อขาย',
  },
};

export type AnalysisLearningStage = {
  no: string;
  title: string;
  summary: string;
  points: string[];
  lessonCount: number;
  target: string;
  linkLabel: string;
};

export const analysisLearningStages: AnalysisLearningStage[] = [
  {
    no: '01',
    title: 'พื้นฐานกราฟและแท่งเทียน',
    summary:
      'ทำความรู้จักแท่งเทียน กรอบเวลา และวิธีอ่านราคาจากกราฟให้ออกก่อนหยิบเครื่องมืออื่น เพราะทุกเครื่องมือที่ตามมาล้วนคำนวณจากราคาเดียวกัน',
    points: ['แท่งเทียนบอกแรงซื้อแรงขายอย่างไร', 'กรอบเวลาแต่ละแบบเหมาะกับอะไร', 'ฝึกนับแท่งเทียนจากกราฟตัวอย่าง'],
    lessonCount: 5,
    target: '#tutorial-candlestick',
    linkLabel: 'เริ่มจากหมวดแท่งเทียน',
  },
  {
    no: '02',
    title: 'แนวโน้มและโครงสร้างตลาด',
    summary:
      'แยกให้ออกว่าตลาดกำลังขึ้น ลง หรือไซด์เวย์ เพราะทิศทางหลักเป็นตัวกำหนดวิธีอ่านสัญญาณทั้งหมด อ่านโครงสร้างได้เร็วจะกรองสัญญาณหลอกได้ดีขึ้น',
    points: ['Higher High และ Lower Low', 'ตลาดไซด์เวย์สังเกตได้จากอะไร', 'จับคู่กรอบเวลาเล็กกับกรอบใหญ่'],
    lessonCount: 5,
    target: '#tutorial-trend',
    linkLabel: 'ไปที่หมวดแนวโน้ม',
  },
  {
    no: '03',
    title: 'แนวรับแนวต้านและอินดิเคเตอร์',
    summary:
      'รู้จักโซนราคาที่ราคามักหยุดหรือกลับตัว พร้อมอินดิเคเตอร์ยอดนิยมอย่าง Moving Average, RSI และ MACD ที่ช่วยยืนยันจังหวะ ไม่ใช่ทำนายทิศทาง',
    points: ['วาดแนวรับแนวต้านจากจุดไหน', 'อินดิเคเตอร์ช่วยยืนยันอะไรได้บ้าง', 'เลือกใช้ไม่เกินสองตัวที่เข้าใจจริง'],
    lessonCount: 5,
    target: '#tutorial-levels',
    linkLabel: 'ไปที่หมวดแนวรับแนวต้าน',
  },
  {
    no: '04',
    title: 'การวิเคราะห์กราฟอย่างเป็นระบบ',
    summary:
      'นำแนวโน้ม โซนราคาสำคัญ และอินดิเคเตอร์มาเรียบเรียงเป็นขั้นตอนเดียว พร้อมยอมรับข้อจำกัดของกราฟก่อนตัดสินใจจากสัญญาณใด ๆ',
    points: ['ลำดับการอ่านกราฟทีละขั้น', 'ข้อจำกัดที่ต้องยอมรับก่อนตัดสินใจ', 'ทบทวนแผนก่อนเปิดออเดอร์ทุกครั้ง'],
    lessonCount: 5,
    target: '#tutorial-method',
    linkLabel: 'ไปที่หมวดเทคนิคการวิเคราะห์',
  },
];

export const analysisCategories: AnalysisCategory[] = [
  {
    id: 'candlestick',
    no: '01',
    title: 'พื้นฐานกราฟและแท่งเทียน',
    intro: 'เริ่มจากสิ่งที่ตาเห็นบนกราฟก่อน เข้าใจว่าแท่งเทียนหนึ่งแท่งบอกอะไร และกรอบเวลาส่งผลต่อการอ่านอย่างไร',
    colSpan: 7,
    tone: 'primary',
    lessons: [
      { title: 'แท่งเทียน Forex คืออะไร และอ่านอย่างไร', slug: 'reading-candlestick-charts' },
      { title: 'วิธีอ่านกราฟแท่งเทียนสำหรับมือใหม่', slug: 'candle-reading-basics' },
      { title: 'กรอบเวลาในกราฟ Forex คืออะไร', slug: 'forex-chart-timeframes' },
      { title: 'แท่งเทียนขาขึ้นและขาลงแตกต่างกันอย่างไร', slug: 'bullish-bearish-candlesticks' },
      { title: 'วิธีเลือกกรอบเวลาให้เหมาะกับการวิเคราะห์', slug: 'forex-timeframe-selection' },
    ],
  },
  {
    id: 'trend',
    no: '02',
    title: 'แนวโน้มและโครงสร้างตลาด',
    intro: 'ทิศทางหลักของราคาคือกรอบความคิดแรกของการวิเคราะห์ ถ้าอ่านแนวโน้มผิด เครื่องมือที่ตามมาจะผิดตามไปด้วย',
    colSpan: 5,
    tone: 'accent',
    lessons: [
      { title: 'วิธีดูแนวโน้มขาขึ้นและขาลงในตลาด Forex', slug: 'forex-trend-direction' },
      { title: 'ตลาด Sideways คืออะไร และสังเกตได้อย่างไร', slug: 'sideways-market' },
      { title: 'วิธีลากเส้นแนวโน้มบนกราฟ Forex', slug: 'trendline-drawing' },
      { title: 'Higher High และ Lower Low คืออะไร', slug: 'higher-high-lower-low' },
      { title: 'วิธีสังเกตการเปลี่ยนแปลงของแนวโน้มราคา', slug: 'trend-reversal-signs' },
    ],
  },
  {
    id: 'levels',
    no: '03',
    title: 'แนวรับและแนวต้าน',
    intro: 'โซนที่ราคาเคยหยุดหรือกลับตัวซ้ำ ๆ มักเป็นจุดอ้างอิงที่ดีกว่าการเดาจากตัวเลขกลม ๆ',
    colSpan: 5,
    tone: 'secondary',
    lessons: [
      { title: 'แนวรับและแนวต้านคืออะไร', slug: 'support-resistance' },
      { title: 'วิธีหาแนวรับแนวต้านบนกราฟ Forex', slug: 'how-to-find-support-resistance' },
      { title: 'แนวรับแนวต้านแบบไดนามิกคืออะไร', slug: 'dynamic-support-resistance' },
      { title: 'การทะลุแนวรับแนวต้านหมายถึงอะไร', slug: 'support-resistance-breakout' },
      { title: 'วิธีสังเกตการทะลุหลอกของราคา', slug: 'false-breakout' },
    ],
  },
  {
    id: 'indicators',
    no: '04',
    title: 'อินดิเคเตอร์ทางเทคนิค',
    intro: 'อินดิเคเตอร์คำนวณจากราคาในอดีต จึงใช้ยืนยันจังหวะ ไม่ใช่แทนการอ่านโครงสร้างราคา',
    colSpan: 7,
    tone: 'primary',
    lessons: [
      { title: 'Moving Average คืออะไร และใช้อย่างไร', slug: 'moving-average-indicator' },
      { title: 'RSI คืออะไร และอ่านค่าอย่างไร', slug: 'rsi-indicator' },
      { title: 'MACD คืออะไร และใช้วิเคราะห์อย่างไร', slug: 'macd-indicator' },
      { title: 'Bollinger Bands คืออะไร', slug: 'bollinger-bands' },
      { title: 'ข้อจำกัดของการใช้ Indicator ในการวิเคราะห์ Forex', slug: 'technical-indicators' },
    ],
  },
  {
    id: 'patterns',
    no: '05',
    title: 'รูปแบบกราฟราคา',
    intro: 'รูปแบบกราฟคือภาพรวมของพฤติกรรมราคาในอดีต ช่วยให้เห็นบริบท แต่ไม่ควรใช้ตัดสินใจเพียงอย่างเดียว',
    colSpan: 4,
    tone: 'accent',
    lessons: [
      { title: 'Double Top และ Double Bottom คืออะไร', slug: 'double-top-double-bottom' },
      { title: 'Head and Shoulders คืออะไร', slug: 'head-and-shoulders' },
      { title: 'Triangle Pattern คืออะไร', slug: 'triangle-pattern' },
      { title: 'Flag Pattern คืออะไร', slug: 'flag-pattern' },
      { title: 'วิธีอ่านรูปแบบกราฟโดยไม่รีบสรุปทิศทางราคา', slug: 'chart-pattern-reading' },
    ],
  },
  {
    id: 'method',
    no: '06',
    title: 'เทคนิคการวิเคราะห์ Forex',
    intro: 'รวมเครื่องมือทั้งหมดเข้าด้วยกันเป็นลำดับการทำงานเดียว ตั้งแต่ดูแนวโน้มจนถึงการยอมรับข้อจำกัดของกราฟ',
    colSpan: 8,
    tone: 'secondary',
    lessons: [
      { title: 'วิธีวิเคราะห์กราฟ Forex ทีละขั้นตอน', slug: 'how-to-analyze-forex-chart' },
      { title: 'วิธีใช้กราฟกำหนดจุด Stop Loss และ Take Profit', slug: 'stop-loss-take-profit' },
      { title: 'วิธีใช้แนวรับแนวต้านร่วมกับอินดิเคเตอร์', slug: 'support-resistance-with-indicators' },
      { title: 'Price Action คืออะไร', slug: 'price-action' },
      { title: 'ข้อผิดพลาดที่พบบ่อยในการวิเคราะห์กราฟ Forex', slug: 'forex-technical-analysis' },
    ],
  },
];

export const analysisChartCandles = [
  { high: 38, low: 26, open: 28, close: 35 },
  { high: 42, low: 32, open: 35, close: 39 },
  { high: 44, low: 36, open: 39, close: 37 },
  { high: 40, low: 28, open: 37, close: 31 },
  { high: 36, low: 26, open: 31, close: 33 },
  { high: 45, low: 31, open: 33, close: 42 },
  { high: 50, low: 40, open: 42, close: 47 },
  { high: 52, low: 44, open: 47, close: 45 },
  { high: 50, low: 40, open: 45, close: 42 },
  { high: 48, low: 38, open: 42, close: 46 },
  { high: 58, low: 44, open: 46, close: 55 },
  { high: 68, low: 54, open: 55, close: 65 },
  { high: 76, low: 62, open: 65, close: 73 },
  { high: 78, low: 68, open: 73, close: 70 },
  { high: 76, low: 64, open: 70, close: 67 },
  { high: 74, low: 62, open: 67, close: 71 },
  { high: 82, low: 68, open: 71, close: 79 },
  { high: 88, low: 76, open: 79, close: 85 },
];

export const chartLevels = {
  resistance: 70,
  support: 42,
};

export const chartCaseMarkers = [
  { no: 1, label: 'แนวโน้มราคา', top: '60%', left: '16%' },
  { no: 2, label: 'แนวรับ', top: '57%', left: '74%' },
  { no: 3, label: 'แนวต้าน', top: '32%', left: '20%' },
  { no: 4, label: 'จุดที่ราคาเคลื่อนไหวผ่านระดับสำคัญ', top: '36%', left: '58%' },
];

export const chartCaseSteps = [
  {
    no: '01',
    title: 'ดูทิศทางรวมของราคาก่อน',
    body: 'เริ่มจากมองว่าจุดสูงและจุดต่ำของราคายกขึ้นหรือกดลงตลอดช่วงที่เห็นในกราฟ ทิศทางนี้เป็นบริบทที่ทำให้สัญญาณอื่นมีความหมาย',
  },
  {
    no: '02',
    title: 'หาพื้นที่ที่ราคาเคยหยุด',
    body: 'ลากเส้นแนวนอนจากจุดที่ราคาแตะแล้วเด้งกลับหลายครั้ง พื้นที่ด้านล่างคือแนวรับ ด้านบนคือแนวต้าน และควรลากเป็นโซนไม่ใช่เส้นเดียว',
  },
  {
    no: '03',
    title: 'อ่านโครงสร้างตอนราคาผ่านระดับ',
    body: 'เมื่อราคาปิดผ่านแนวต้านได้ ให้สังเกตว่ามีแรงตามมาหรือกลับลงมาเร็ว ถ้ากลับลงเร็วคือการทะลุหลอก ซึ่งเป็นกรณีที่พบบ่อย',
  },
  {
    no: '04',
    title: 'ยอมรับว่ากราฟไม่ใช่การพยากรณ์',
    body: 'ทุกสัญญาณเป็นเรื่องของความน่าจะเป็น ไม่มีรูปแบบใดยืนยันอนาคตได้ การวิเคราะห์มีไว้จัดลำดับความเป็นไปได้และกำหนดจุดที่คิดผิดไว้ล่วงหน้า',
  },
];

export const analysisIndicators = [
  {
    id: 'ma' as const,
    name: 'Moving Average',
    tag: 'อินดิเคเตอร์ตามแนวโน้ม',
    description:
      'เส้นค่าเฉลี่ยราคาในช่วงเวลาที่กำหนด ช่วยให้เห็นทิศทางหลักชัดขึ้นเมื่อราคาแกว่งแรง และใช้เป็นแนวรับแนวต้านแบบเคลื่อนที่ได้',
    note: 'ยิ่งค่าเฉลี่ยยาว ยิ่งตอบสนองช้า แต่สัญญาณหลอกน้อยลง',
    slug: 'moving-average-indicator',
    series: [30, 31, 30.5, 32, 33.4, 33, 34.2, 35.6, 35.1, 36.8, 38.2, 37.9, 39.4, 41, 42.6, 43.1],
  },
  {
    id: 'rsi' as const,
    name: 'RSI',
    tag: 'อินดิเคเตอร์วัดโมเมนตัม',
    description:
      'วัดความแรงของโมเมนตัมด้วยค่าตั้งแต่ 0 ถึง 100 ค่าเกิน 70 มักถูกอ่านว่าเข้าสู่โซนซื้อมากเกินไป และต่ำกว่า 30 คือขายมากเกินไป',
    note: 'ในตลาดแนวโน้มแรง ค่าอาจค้างในโซนสุดโต่งได้นาน',
    slug: 'technical-indicators',
    series: [48, 52, 58, 63, 68, 74, 78, 72, 66, 61, 57, 62, 69, 75, 71, 64],
  },
  {
    id: 'macd' as const,
    name: 'MACD',
    tag: 'โมเมนตัมและแนวโน้ม',
    description:
      'คำนวณจากเส้นค่าเฉลี่ยสองเส้นมาหาความต่าง แสดงเป็นเส้น MACD เส้นสัญญาณ และฮิสโตแกรมที่บอกรูปโมเมนตัมในช่วงนั้น',
    note: 'สัญญาณตัดกันช้ากว่าราคา จึงเหมาะกับการยืนยัน ไม่ใช่การเข้าออกเร็ว',
    slug: 'technical-indicators',
    series: [0.6, 1.4, 2.2, 2.9, 3.1, 2.4, 1.2, 0.3, -0.4, -0.2, 0.7, 1.8, 2.6, 3.2, 2.8, 1.9],
  },
];

export const analysisMistakes = [
  {
    title: 'การใช้อินดิเคเตอร์หลายตัวมากเกินไป',
    body: 'การวาง RSI MACD และ Stochastic พร้อมกันมักให้สัญญาณขัดแย้งกันเอง จนตัดสินใจไม่ได้ ใช้เพียงหนึ่งหรือสองตัวที่เข้าใจจริงจะชัดกว่า',
    to: '/technical-indicators',
  },
  {
    title: 'การวิเคราะห์โดยไม่ดูแนวโน้มหลัก',
    body: 'สัญญาณกลับตัวบนกรอบเวลาเล็กมักจบเร็ว ถ้าไม่ดูทิศทางจากกรอบใหญ่ก่อน จะเข้าออเดอร์สวนแนวโน้มโดยไม่รู้ตัว',
    to: '/forex-trend-direction',
  },
  {
    title: 'การเข้าใจแนวรับแนวต้านผิดพลาด',
    body: 'แนวรับแนวต้านคือโซน ไม่ใช่เส้นที่ต้องแม่นถึงจุดทศนิยม และเมื่อถูกเบรกแล้ว ระดับเดิมอาจกลายเป็นแนวตรงข้ามได้',
    to: '/support-resistance',
  },
  {
    title: 'การตัดสินใจจากสัญญาณเพียงครั้งเดียว',
    body: 'การตัดกันของเส้นหรือแท่งเทียนแท่งเดียวไม่ใช่หลักฐานพอ ควรดูบริบทร่วมกับโครงสร้างราคาและกรอบเวลาใหญ่',
    to: '/forex-technical-analysis',
  },
  {
    title: 'การละเลยการบริหารความเสี่ยง',
    body: 'วิเคราะห์ดีแค่ไหนก็ผิดได้เสมอ สิ่งที่กำหนดผลลัพธ์ระยะยาวคือขนาดความเสี่ยงต่อออเดอร์และวินัยในการทำตามแผน',
    to: '/risk-management-basics',
  },
];

export const analysisRelatedTracks = [
  {
    title: 'Forex พื้นฐาน',
    description: 'ปูพื้นเรื่องคู่เงิน Pip Spread และ Leverage ก่อนลงลึกกับการอ่านกราฟ',
    path: '/forex-basics',
    icon: 'ri-book-open-line',
    meta: '6 หมวดพื้นฐาน',
  },
  {
    title: 'MT4 / MT5',
    description: 'เรียนวิธีเปิดกราฟ เพิ่มอินดิเคเตอร์ และวาดเส้นบนแพลตฟอร์มจริง',
    path: '/mt4-mt5',
    icon: 'ri-computer-line',
    meta: 'คู่มือแพลตฟอร์ม',
  },
  {
    title: 'การบริหารความเสี่ยง',
    description: 'กำหนดความเสี่ยงต่อออเดอร์และขนาดสถานะให้พอดีกับพอร์ต',
    path: '/risk-management',
    icon: 'ri-shield-check-line',
    meta: 'วางแผนก่อนเทรด',
  },
];